// components/skillboard/mobile/MobileSkillAchievements.tsx - Responsive Fixed Version
import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Chip,
  Avatar,
  LinearProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { POST } from "api/index";
import { usePoints } from "context/PointsContext";
import { useTranslation } from "react-i18next";
import ImageComponent from "ui/components/shared/ImageComponent";

const ModernTitle = styled(Typography)({
  fontSize: '2.2rem',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #A1B7A5 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: '1.25rem',
  letterSpacing: '-0.025em',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  
  '@media (max-width: 390px)': {
    fontSize: '2rem',
    marginBottom: '1rem'
  },
  
  '@media (max-width: 375px)': {
    fontSize: '1.8rem',
    marginBottom: '0.875rem'
  },
  
  '@media (max-width: 320px)': {
    fontSize: '1.6rem',
    marginBottom: '0.75rem',
    letterSpacing: '-0.02em'
  },
  
  '@media (max-width: 280px)': {
    fontSize: '1.4rem',
    marginBottom: '0.625rem',
    letterSpacing: '-0.015em'
  }
});



// Hero Points Card - Responsive padding and font sizes
const HeroPointsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  background: 'linear-gradient(135deg, #B39DDB 0%, #9575CD 100%)',
  color: 'white',
  boxShadow: '0 12px 40px rgba(179, 157, 219, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    borderRadius: 20,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -30,
    right: -30,
    width: 80,
    height: 80,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    [theme.breakpoints.up('sm')]: {
      top: -50,
      right: -50,
      width: 100,
      height: 100,
    }
  }
}));

// Responsive metric cards with better text scaling
const MetricCard = styled(Paper)(({ theme, cardType }: { theme?: any, cardType: 'progress' | 'streak' }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  minHeight: 80,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
    gap: theme.spacing(2),
    minHeight: 90,
  },
  '&:hover': {
    transform: 'translateY(-1px)',
  },
  ...(cardType === 'progress' && {
    background: `linear-gradient(135deg, #4CAF50 0%, ${theme.palette.primary.main} 80%)`,
    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
    '&:hover': {
      boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
    }
  }),
  ...(cardType === 'streak' && {
    background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
    boxShadow: '0 6px 20px rgba(255, 107, 107, 0.3)',
    '&:hover': {
      boxShadow: '0 8px 25px rgba(255, 107, 107, 0.4)',
    }
  }),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -15,
    right: -15,
    width: 40,
    height: 40,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    [theme.breakpoints.up('sm')]: {
      top: -20,
      right: -20,
      width: 60,
      height: 60,
    }
  }
}));

// Responsive achievement cards
const AchievementCard = styled(Paper)(({ theme }) => ({
  minWidth: 140,
  maxWidth: 140,
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: 'white',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  textAlign: 'center',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  [theme.breakpoints.up('sm')]: {
    minWidth: 160,
    maxWidth: 160,
    padding: theme.spacing(2),
    borderRadius: 16,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
  }
}));

interface CourseLike {
  id: number;
  title: string;
  finished_at?: string;
  image?: string;
}

const MobileSkillAchievements: React.FC = () => {
  const { totalPoints } = usePoints();
  const [streak, setStreak] = useState<number>(0);
  const [weeklyPoints, setWeeklyPoints] = useState<number>(0);
  const [achievements, setAchievements] = useState<CourseLike[]>([]);
  const { t } = useTranslation();
  const token = window.localStorage.getItem("currentUserToken");

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      try {
        const body = new URLSearchParams();
        body.append("userToken", token);

        const [streakResponse, weeklyResponse, coursesResponse] = await Promise.all([
          POST("getUserStreak", body),
          POST("getWeeklyUserPoints", body),
          POST("getCompletedCourses", body)
        ]);

        if (streakResponse.status === 200) setStreak(streakResponse.streak);
        if (weeklyResponse.status === 200) {
          setWeeklyPoints(
            weeklyResponse.weekly_points.total_course_points + 
            weeklyResponse.weekly_points.total_interactive_points
          );
        }
        setAchievements(coursesResponse.result || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [token]);

  const formatValue = (value: number): string => {
    return value.toLocaleString('de-DE');
  };


  return (
    <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
      {/* Section Header - Responsive typography */}
     
       <ModernTitle sx={{textAlign:"left"}}>Skillboard</ModernTitle>

      {/* Hero Points Card - Responsive design */}
      <HeroPointsCard sx={{ mb: 2.5 , mt: 2.5}}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 1.5, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}>
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              width: { xs: 40, sm: 48 }, 
              height: { xs: 40, sm: 48 }, 
              mr: 2,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}>
              💎
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  opacity: 0.9,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                {t("skillboard.achievements.learningPoints")}
              </Typography>
            </Box>
          </Box>
          
          <Typography 
            variant="h2" 
            fontWeight="900" 
            sx={{ 
              mb: { xs: 1.5, sm: 2 }, 
              fontSize: { xs: '2rem', sm: '2.5rem' },
              wordBreak: 'break-word'
            }}
          >
            {formatValue(Number(totalPoints))}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={75} 
              sx={{ 
                flexGrow: 1, 
                height: { xs: 6, sm: 8 }, 
                borderRadius: { xs: 3, sm: 4 },
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: { xs: 3, sm: 4 }
                }
              }} 
            />
            <Typography 
              variant="caption" 
              sx={{ 
                opacity: 0.9, 
                minWidth: { xs: '50px', sm: '60px' },
                fontSize: { xs: '0.7rem', sm: '0.75rem' }
              }}
            >
              Level 3
            </Typography>
          </Box>
        </Box>
      </HeroPointsCard>

      {/* Responsive Stats Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 2.5 }}>
        <Grid item xs={6}>
          <MetricCard cardType="progress">
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white',    
              width: { xs: 36, sm: 44 }, 
              height: { xs: 36, sm: 44 }
            }}>
              <TrendingUpIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  lineHeight: 1.2
                }} 
                noWrap
              >
              {t("skillboard.achievements.thisWeek")}
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="700" 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.1
                }}
                noWrap
              >
                {weeklyPoints > 0 ? `+${formatValue(weeklyPoints)}` : '0'}
              </Typography>
            </Box>
          </MetricCard>
        </Grid>
        
        <Grid item xs={6}>
          <MetricCard cardType="streak">
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)',   
              color: 'white',    
              width: { xs: 36, sm: 44 }, 
              height: { xs: 36, sm: 44 }
            }}>
              <LocalFireDepartmentIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  lineHeight: 1.2
                }} 
                noWrap
              >
              {t("skillboard.achievements.streak")}
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="700" 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.1
                }}
                noWrap
              >
                {streak}
              </Typography>
            </Box>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Achievements Section - Responsive */}
      {achievements.length > 0 && (
        <Box >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Typography 
              variant="h6" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
                🏅 {t("skillboard.achievements.achievementsBadge")} ({achievements.length})

            </Typography>

          </Box>
          
          <Box 
            sx={{ 
              display: 'flex', 
              gap: { xs: 1.5, sm: 2 }, 
              overflowX: 'auto',
              pb: 2,
              pt: 2,
              mx: { xs: -1, sm: 0 },
              '&::-webkit-scrollbar': {
                height: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 2,
              }

            }}
          >
            {achievements.slice(0, achievements.length).map((achievement) => (
              <AchievementCard key={achievement.id}>
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  {achievement.image ? (
                    <ImageComponent
                      src={achievement.image}
                      alt={achievement.title}
                      width="50px"
                      height="50px"
                      borderRadius="10px"
                    />
                  ) : (
                    <Avatar sx={{ 
                      width: 50, 
                      height: 50, 
                      bgcolor: 'primary.main', 
                      mx: 'auto' 
                    }}>
                      <EmojiEventsIcon sx={{ fontSize: 24 }} />
                    </Avatar>
                  )}
                </Box>
                
                <Typography 
                  variant="body2" 
                  fontWeight="600"
                  sx={{ 
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                    minHeight: '2.6em',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  {achievement.title}
                </Typography>
                
                {achievement.finished_at && (
                  <Chip 
                    label={new Date(achievement.finished_at).toLocaleDateString('de-DE')}
                    size="small"
                    sx={{ 
                      bgcolor: 'success.main', 
                      color: 'white', 
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      height: { xs: 20, sm: 24 }
                    }}
                  />
                )}
              </AchievementCard>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MobileSkillAchievements;